import { useEffect, useState } from 'react';
import type { PaymentMethodBrand } from '../../types/registration-payment.types';
import { useStartLink } from './api/hooks';
import type { StartLinkBody } from './api/registration.api';
import type {
  AccountState,
  FlowEvent,
  RegistrationContext
} from './flow/types';

type Send = (event: FlowEvent, patch?: Partial<AccountState>) => void;

const linkBody = (
  context: RegistrationContext,
  brand: PaymentMethodBrand
): StartLinkBody =>
  context.entry === 'onglaye'
    ? {
        destination: 'onglayeRegistration',
        payment_method: brand,
        ong_key: context.ongKey ?? undefined
      }
    : {
        destination: 'checkoutRegistration',
        payment_method: brand,
        checkout_session_id: context.checkout?.sessionId
      };

/**
 * Starts linking the picked payment method and hands the browser to the
 * gateway. The important notes are confirmed by the picker before `pick` is
 * called, so linking starts straight away.
 */
export function useLinkMethod(
  context: RegistrationContext | null,
  send: Send
): { pick: (brand: PaymentMethodBrand) => void; busy: boolean } {
  const startLink = useStartLink();
  const [redirecting, setRedirecting] = useState(false);

  // Back from the gateway can restore this page from the browser's
  // back-forward cache with the redirect still marked in progress.
  useEffect(() => {
    const onPageShow = (event: PageTransitionEvent) => {
      if (event.persisted) setRedirecting(false);
    };
    window.addEventListener('pageshow', onPageShow);
    return () => window.removeEventListener('pageshow', onPageShow);
  }, []);

  const fail = (code: string | null) =>
    send('methodFailed', { failure: { code, desc: null, cancelled: false } });

  const pick = (brand: PaymentMethodBrand) => {
    const accessToken = context?.account?.accessToken;
    if (!context || !accessToken || startLink.isPending || redirecting) {
      if (context && !accessToken) fail(null);
      return;
    }
    startLink.mutate(
      { body: linkBody(context, brand), accessToken },
      {
        onSuccess: ({ payUrl }) => {
          if (!payUrl) {
            fail(null);
            return;
          }
          setRedirecting(true);
          window.location.assign(payUrl);
        },
        onError: (err) => fail(err.code)
      }
    );
  };

  return { pick, busy: startLink.isPending || redirecting };
}
